import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Chip, 
  Button,
  Paper,
  TextField 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { jobService } from '../services/job';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const jobData = await jobService.getJob(id);
        setJob(jobData);
        setEditedJob({
          ...jobData,
          statusId: jobData.statusId || 1 // Add default statusId if not present
        });
      } catch (error) {
        console.error('Failed to fetch job:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  const handleSave = async () => {
    try {
      await jobService.updateJob(id, editedJob);
      setJob(editedJob);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update job:', error);
    }
  };

  const handleChange = (field) => (event) => {
    setEditedJob({
      ...editedJob,
      [field]: event.target.value
    });
  };

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Job not found</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/jobs')}
        sx={{ mb: 3 }}
      >
        Back to Jobs
      </Button>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isEditing ? (
              <TextField
                value={editedJob.title}
                onChange={handleChange('title')}
                variant="standard"
                sx={{ fontSize: 'h4.fontSize' }}
              />
            ) : (
              <Typography variant="h4" component="h1">
                {job.title}
              </Typography>
            )}
            <Button 
              variant="contained" 
              color={isEditing ? "success" : "primary"}
              onClick={() => {
                if (isEditing) {
                  handleSave();
                } else {
                  setIsEditing(true);
                }
              }}
            >
              {isEditing ? "Save" : "Edit"}
            </Button>
            {isEditing && (
              <Button 
                variant="outlined" 
                color="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setEditedJob(job);
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
          {isEditing ? (
            <TextField
              value={editedJob.location}
              onChange={handleChange('location')}
              variant="standard"
            />
          ) : (
            <Chip
              label={job.location}
              color={job.location.toLowerCase() === 'remote' ? 'success' : 'default'}
              sx={{ borderRadius: 1 }}
            />
          )}
        </Box>

        {isEditing ? (
          <>
            <TextField
              fullWidth
              label="Company Name"
              value={editedJob.companyName}
              onChange={handleChange('companyName')}
              variant="standard"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Company Description"
              value={editedJob.companyDesc}
              onChange={handleChange('companyDesc')}
              multiline
              rows={3}
              variant="standard"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Job Description"
              value={editedJob.description}
              onChange={handleChange('description')}
              multiline
              rows={4}
              variant="standard"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Requirements"
              value={editedJob.requirements}
              onChange={handleChange('requirements')}
              multiline
              rows={4}
              variant="standard"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Minimum Salary"
                value={editedJob.salaryRangeMin}
                onChange={handleChange('salaryRangeMin')}
                variant="standard"
              />
              <TextField
                label="Maximum Salary"
                value={editedJob.salaryRangeMax}
                onChange={handleChange('salaryRangeMax')}
                variant="standard"
              />
            </Box>
            <TextField
              fullWidth
              label="Status ID"
              value={editedJob.statusId}
              onChange={handleChange('statusId')}
              variant="standard"
              sx={{ mb: 2 }}
              type="number"
            />
          </>
        ) : (
          <>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {job.companyName}
            </Typography>

            <Typography variant="body1" paragraph>
              {job.companyDesc}
            </Typography>

            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" paragraph>
              {job.description}
            </Typography>

            <Typography variant="h6" gutterBottom>
              Requirements
            </Typography>
            <Typography variant="body1" paragraph>
              {job.requirements}
            </Typography>

            {job.salaryRangeMin !== "0.00" && job.salaryRangeMax !== "0.00" && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Salary Range
                </Typography>
                <Typography variant="body1">
                  ${job.salaryRangeMin} - ${job.salaryRangeMax}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" paragraph>
              Status ID: {job.statusId}
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default JobDetail; 